import { areaDistance, type Area } from "./area";

// The sim tier of world streaming (spike 002, gate C1): which chunks are resident in the simulation (their colliders,
// nav tiles and whatever else the game hangs on them), decided on sim steps from sim state only, so a deterministic
// sim stays deterministic. The view tier (`Streamer`) streams meshes on its own radii under a per-frame time budget;
// this tier streams what the sim needs under a per-step budget counted in loads, never in milliseconds.
//
// - Anchors: the player (and the player `predictS` ahead) asks for every chunk within `request`; pins (other
//   characters, a camp waiting for its fighters) keep the chunks within `pinRadius` of them resident. A chunk past
//   `unload` from the player and the prediction, and past twice `pinRadius` from every pin, unloads.
// - A step: unloads first, then loads: what a pin or the player stands on (or is within `inBy` of) loads at once,
//   the rest from a queue, nearest first, `loadsPerStep` a step. A chunk within `inBy` of the player that isn't
//   resident when the step starts is late (counted, and loaded at once).
// - Layers: the game's loaders, applied in order on a load (physics first) and in reverse on an unload, so a chunk's
//   colliders, nav tile and events come and go on the same step. `consistent()` reports any layer out of step.
// - prime(): blocking, at boot and on a teleport (a jump over `teleportM`: a cut), loading everything wanted now.
// - Saves: `snapshot()` records the resident set and each layer's; `restore()` takes it back, re-applying the layers
//   whose state didn't come back with another snapshot (a nav tile; the colliders come back in the physics').
// - Faults: `lateLayer` names a layer applied one chunk late: each load applies that layer to the chunk loaded before
//   it (the game's "colliders built one chunk late" defect: the C1 checks must catch it).

/** The sim tier's radii (m) and per-step budget. */
export interface ResidencyConfig {
  /** Chunks within this distance of the player, or of the player `predictS` ahead, load. */
  request: number;
  /** A chunk within this distance of the player must be resident: one that isn't, as a step starts, is late. */
  inBy: number;
  /** Chunks further than this from the player and the prediction (and from every pin) unload. */
  unload: number;
  /** Seconds of velocity for the predicted anchor. */
  predictS: number;
  /** Pins keep the chunks within this distance resident (loaded at once). */
  pinRadius: number;
  /** Queued loads per step (pins and must-have chunks aren't held to it). */
  loadsPerStep: number;
  /** A jump of more than this many metres between steps is a teleport (a cut: a prime). */
  teleportM: number;
  /** Steps after a prime before lateness counts. */
  graceSteps: number;
}

/** Defaults (the agent's, spike 002): a physics tier inside the view tier's 85 / 60 / 100. */
export const RESIDENCY_DEFAULTS: ResidencyConfig = { request: 72, inBy: 40, unload: 90, predictS: 1.5, pinRadius: 6, loadsPerStep: 1, teleportM: 12, graceSteps: 0 };

/** A chunk as the sim tier sees it: an id and its walkable rectangle. */
export interface ResidentChunk {
  id: string;
  area: Area;
}

/** One kind of thing a chunk carries in the sim (its colliders, its nav tile, its events), loaded and unloaded by the
 *  game. Layers apply in the order given on a load and in reverse on an unload. */
export interface ResidencyLayer {
  name: string;
  load(id: string): void;
  unload(id: string): void;
}

/** A point the tier streams around: the player (with its velocity), or a pin. */
export interface ResidencyAnchor {
  x: number;
  z: number;
  vx?: number;
  vz?: number;
}

/** What a step or a prime changed, in the order applied. */
export interface ResidencyChange {
  loaded: string[];
  unloaded: string[];
}

/** The sim tier's saved state. JSON-safe. */
export interface ResidencySnapshot {
  format: "rpg.residency@1";
  /** Resident chunks, in chunk order. */
  loaded: string[];
  /** Each layer's applied chunks (equal to `loaded` unless a layer is out of step). */
  layers: Record<string, string[]>;
  /** The chunk a late layer holds back (the planted defect), by layer. */
  held: Record<string, string | null>;
  last: { x: number; z: number } | null;
  grace: number;
  steps: number;
}

/** The snapshot format tag. */
export const RESIDENCY_SNAPSHOT_FORMAT = "rpg.residency@1" as const;

/** What the tier measured (diagnostics: not saved). */
export interface ResidencyStats {
  steps: number;
  loads: number;
  unloads: number;
  /** Chunks found within `inBy` of the player but not resident at a step's start. */
  late: number;
  lateLog: { step: number; id: string; d: number }[];
  /** Primes: at boot and on teleports. */
  cuts: { step: number; reason: string; loaded: number }[];
  /** The most loads applied in one step (outside primes). */
  maxLoadsPerStep: number;
  /** The most chunks resident at once. */
  peakResident: number;
}

const r2 = (v: number): number => Math.round(v * 100) / 100;

/** The sim tier: decides on each sim step which chunks are resident and applies the game's layers to them. */
export class Residency {
  readonly cfg: ResidencyConfig;
  /** Planted defects. `lateLayer`: the named layer is applied one chunk late. */
  readonly faults: { lateLayer: string | null } = { lateLayer: null };
  private readonly index = new Map<string, number>();
  private loaded = new Set<string>();
  private applied = new Map<string, Set<string>>();
  private held = new Map<string, string | null>();
  private last: { x: number; z: number } | null = null;
  private grace = 0;
  private steps = 0;
  private st: ResidencyStats = { steps: 0, loads: 0, unloads: 0, late: 0, lateLog: [], cuts: [], maxLoadsPerStep: 0, peakResident: 0 };

  constructor(
    private readonly chunks: readonly ResidentChunk[],
    private readonly layers: readonly ResidencyLayer[],
    cfg: Partial<ResidencyConfig> = {},
  ) {
    this.cfg = { ...RESIDENCY_DEFAULTS, ...cfg };
    if (this.cfg.unload <= this.cfg.request) throw new Error("residency: unload must be past request (hysteresis)");
    chunks.forEach((c, i) => {
      if (this.index.has(c.id)) throw new Error(`residency: chunk "${c.id}" listed twice`);
      this.index.set(c.id, i);
    });
    for (const l of layers) {
      if (this.applied.has(l.name)) throw new Error(`residency: layer "${l.name}" listed twice`);
      this.applied.set(l.name, new Set());
      this.held.set(l.name, null);
    }
  }

  /** Blocking: load every chunk wanted around `player` and `pins` now, unload the rest (boot, a teleport, a warp). */
  prime(player: ResidencyAnchor, pins: readonly ResidencyAnchor[] = [], reason = "prime"): ResidencyChange {
    const out: ResidencyChange = { loaded: [], unloaded: [] };
    const want = this.chunks.filter((c) => this.wanted(c, player, pins));
    const keep = new Set(want.map((c) => c.id));
    for (const c of this.chunks) if (this.loaded.has(c.id) && !keep.has(c.id) && this.past(c, player, pins)) this.unloadOne(c.id, out);
    for (const c of want) if (!this.loaded.has(c.id)) this.loadOne(c.id, out);
    this.last = { x: player.x, z: player.z };
    this.grace = this.cfg.graceSteps;
    this.st.cuts.push({ step: this.steps, reason, loaded: out.loaded.length });
    if (this.st.cuts.length > 64) this.st.cuts.shift();
    this.st.peakResident = Math.max(this.st.peakResident, this.loaded.size);
    return out;
  }

  /** One sim step: unload what's past, load what's needed (late and pinned chunks at once, the queue on budget). */
  step(player: ResidencyAnchor, pins: readonly ResidencyAnchor[] = []): ResidencyChange {
    this.steps++;
    this.st.steps = this.steps;
    if (this.grace > 0) this.grace--;
    if (this.last && Math.hypot(player.x - this.last.x, player.z - this.last.z) > this.cfg.teleportM) return this.prime(player, pins, "teleport");
    this.last = { x: player.x, z: player.z };
    const out: ResidencyChange = { loaded: [], unloaded: [] };
    // unloads first
    for (const c of this.chunks) if (this.loaded.has(c.id) && this.past(c, player, pins)) this.unloadOne(c.id, out);
    // then what must be resident now: under or near the player (late if missing), within a pin's radius
    const queue: { c: ResidentChunk; d: number }[] = [];
    for (const c of this.chunks) {
      if (this.loaded.has(c.id)) continue;
      const dh = areaDistance(c.area, player.x, player.z);
      if (dh <= this.cfg.inBy) {
        if (this.grace === 0) {
          this.st.late++;
          if (this.st.lateLog.length < 32) this.st.lateLog.push({ step: this.steps, id: c.id, d: r2(dh) });
        }
        this.loadOne(c.id, out);
        continue;
      }
      if (this.pinned(c, pins)) {
        this.loadOne(c.id, out);
        continue;
      }
      if (this.wanted(c, player, pins)) queue.push({ c, d: dh });
    }
    // the queue: nearest first (ties in chunk order), on the step's budget
    queue.sort((a, b) => a.d - b.d || this.index.get(a.c.id)! - this.index.get(b.c.id)!);
    for (let i = 0; i < queue.length && i < this.cfg.loadsPerStep; i++) this.loadOne(queue[i]!.c.id, out);
    this.st.maxLoadsPerStep = Math.max(this.st.maxLoadsPerStep, out.loaded.length);
    this.st.peakResident = Math.max(this.st.peakResident, this.loaded.size);
    return out;
  }

  /** Whether a chunk is resident. */
  has(id: string): boolean {
    return this.loaded.has(id);
  }

  /** Whether a layer has been applied to a chunk. */
  layerHas(layer: string, id: string): boolean {
    return this.applied.get(layer)?.has(id) === true;
  }

  /** The resident chunks, in chunk order. */
  resident(): string[] {
    return this.chunks.filter((c) => this.loaded.has(c.id)).map((c) => c.id);
  }

  /** Layers out of step with the resident set: "<layer> missing <id>" or "<layer> extra <id>". Empty when in step. */
  consistent(): string[] {
    const out: string[] = [];
    for (const [name, set] of this.applied) {
      for (const id of this.loaded) if (!set.has(id)) out.push(`${name} missing ${id}`);
      for (const id of set) if (!this.loaded.has(id)) out.push(`${name} extra ${id}`);
    }
    return out;
  }

  snapshot(): ResidencySnapshot {
    const order = (s: Iterable<string>): string[] => [...s].sort((a, b) => this.index.get(a)! - this.index.get(b)!);
    return {
      format: RESIDENCY_SNAPSHOT_FORMAT,
      loaded: order(this.loaded),
      layers: Object.fromEntries([...this.applied].map(([n, s]) => [n, order(s)])),
      held: Object.fromEntries(this.held),
      last: this.last ? { ...this.last } : null,
      grace: this.grace,
      steps: this.steps,
    };
  }

  /**
   * Take a snapshot back. Layers named in `restored` came back with another snapshot (the colliders with the physics
   * world's), so only their record is set; every other layer is brought to the saved set by unloading what it has
   * extra and loading what it lacks (a nav tile rebuilt from the same data).
   */
  restore(s: ResidencySnapshot, o: { restored?: readonly string[] } = {}): void {
    if (!s || s.format !== RESIDENCY_SNAPSHOT_FORMAT) throw new Error(`residency: not a residency snapshot (format ${String((s as { format?: unknown })?.format)})`);
    for (const id of s.loaded) if (!this.index.has(id)) throw new Error(`residency: the snapshot names an unknown chunk "${id}"`);
    const restored = new Set(o.restored ?? []);
    for (const l of this.layers) {
      const want = new Set(s.layers[l.name] ?? s.loaded);
      const have = this.applied.get(l.name)!;
      if (!restored.has(l.name)) {
        for (const id of [...have].sort((a, b) => this.index.get(a)! - this.index.get(b)!)) if (!want.has(id)) l.unload(id);
        for (const id of [...want].sort((a, b) => this.index.get(a)! - this.index.get(b)!)) if (!have.has(id)) l.load(id);
      }
      this.applied.set(l.name, want);
      this.held.set(l.name, s.held[l.name] ?? null);
    }
    this.loaded = new Set(s.loaded);
    this.last = s.last ? { ...s.last } : null;
    this.grace = s.grace;
    this.steps = s.steps;
  }

  /** The measurements so far. */
  stats(): ResidencyStats & { resident: number; config: ResidencyConfig } {
    return { ...structuredClone(this.st), resident: this.loaded.size, config: this.cfg };
  }

  /** Clear the measurements (not the chunks). */
  resetStats(): void {
    this.st = { steps: this.steps, loads: 0, unloads: 0, late: 0, lateLog: [], cuts: [], maxLoadsPerStep: 0, peakResident: this.loaded.size };
  }

  private pinned(c: ResidentChunk, pins: readonly ResidencyAnchor[]): boolean {
    for (const p of pins) if (areaDistance(c.area, p.x, p.z) <= this.cfg.pinRadius) return true;
    return false;
  }

  private wanted(c: ResidentChunk, player: ResidencyAnchor, pins: readonly ResidencyAnchor[]): boolean {
    const px = player.x + (player.vx ?? 0) * this.cfg.predictS;
    const pz = player.z + (player.vz ?? 0) * this.cfg.predictS;
    const d = Math.min(areaDistance(c.area, player.x, player.z), areaDistance(c.area, px, pz));
    return d <= this.cfg.request || this.pinned(c, pins);
  }

  /** Past every anchor's unload distance. */
  private past(c: ResidentChunk, player: ResidencyAnchor, pins: readonly ResidencyAnchor[]): boolean {
    const px = player.x + (player.vx ?? 0) * this.cfg.predictS;
    const pz = player.z + (player.vz ?? 0) * this.cfg.predictS;
    if (Math.min(areaDistance(c.area, player.x, player.z), areaDistance(c.area, px, pz)) <= this.cfg.unload) return false;
    for (const p of pins) if (areaDistance(c.area, p.x, p.z) <= this.cfg.pinRadius * 2) return false;
    return true;
  }

  private loadOne(id: string, out: ResidencyChange): void {
    this.loaded.add(id);
    for (const l of this.layers) {
      if (this.faults.lateLayer === l.name) {
        // the planted defect: this layer gets the chunk loaded before this one, and holds this one back
        const prev = this.held.get(l.name) ?? null;
        this.held.set(l.name, id);
        if (prev !== null && this.loaded.has(prev) && !this.applied.get(l.name)!.has(prev)) {
          l.load(prev);
          this.applied.get(l.name)!.add(prev);
        }
        continue;
      }
      l.load(id);
      this.applied.get(l.name)!.add(id);
    }
    this.st.loads++;
    out.loaded.push(id);
  }

  private unloadOne(id: string, out: ResidencyChange): void {
    for (let i = this.layers.length - 1; i >= 0; i--) {
      const l = this.layers[i]!;
      if (this.held.get(l.name) === id) this.held.set(l.name, null);
      if (!this.applied.get(l.name)!.has(id)) continue;
      l.unload(id);
      this.applied.get(l.name)!.delete(id);
    }
    this.loaded.delete(id);
    this.st.unloads++;
    out.unloaded.push(id);
  }
}
